import { format } from 'date-fns'
import { formatInTimeZone } from 'date-fns-tz'

interface BuildCsvFilenameOptions {
  building?: string | null
  page: string
  date?: Date | number | string | null
  dateRange?: [Date | number | string | null, Date | number | string | null]
  timezone?: string
  context?: Array<string | number | null | undefined>
}

const toFilenameToken = (
  value: string | number | null | undefined,
  fallback = '',
  capitalizeParts = false,
): string => {
  const baseToken = String(value ?? '')
    .trim()
    .replace(/['"]/g, '')
    .replace(/[^a-zA-Z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')

  const token = capitalizeParts
    ? baseToken
        .split('-')
        .filter(Boolean)
        .map((part) => `${part.charAt(0).toUpperCase()}${part.slice(1)}`)
        .join('-')
    : baseToken

  return token || fallback
}

const toDate = (value: Date | number | string | null | undefined): Date | null => {
  if (value == null) return null
  const date = value instanceof Date ? value : new Date(value)

  return Number.isNaN(date.getTime()) ? null : date
}

const formatDateToken = (value: Date | number | string, timezone?: string): string => {
  const date = toDate(value)
  if (!date) return ''

  return timezone ? formatInTimeZone(date, timezone, 'MM-dd-yyyy') : format(date, 'MM-dd-yyyy')
}

export const buildCsvFilename = ({
  building,
  page,
  date,
  dateRange,
  timezone,
  context = [],
}: BuildCsvFilenameOptions): string => {
  const buildingToken = toFilenameToken(building, 'All')
  const pageToken = toFilenameToken(page, 'Export', true)

  const [rangeStart, rangeEnd] = dateRange ?? []
  const rangeStartDate = toDate(rangeStart)
  const rangeEndDate = toDate(rangeEnd)

  let dateToken = ''

  if (rangeStartDate && rangeEndDate) {
    dateToken = `${formatDateToken(rangeStartDate, timezone)}_${formatDateToken(rangeEndDate, timezone)}`
  }

  if (!dateToken && date) {
    dateToken = formatDateToken(date, timezone)
  }

  const contextTokens = context.map((token) => toFilenameToken(token)).filter(Boolean)

  const restParts = [
    ...(contextTokens.length ? [`[${contextTokens.join('-')}]`] : []),
    ...(dateToken ? [dateToken] : []),
  ]
  const suffix = restParts.length ? `_${restParts.join('_')}` : ''

  return `[${buildingToken}][${pageToken}]${suffix}.csv`
}
